/**
 * Klickbare Spaltenköpfe und Suchfeld für Personenlisten (`lib/sortierung.ts`).
 * Ein Klick sortiert nach der Spalte, ein zweiter dreht die Richtung. Beträge
 * beginnen mit „hoch nach tief“, Namen mit A bis Z.
 */
import { useState, type ReactNode } from "react";
import type { Richtung } from "../lib/sortierung";

export interface Sortierung<K extends string> {
  spalte: K;
  richtung: Richtung;
}

export function useSortierung<K extends string>(start: Sortierung<K>) {
  const [sortierung, setSortierung] = useState<Sortierung<K>>(start);
  const umschalten = (spalte: K, ersteRichtung: Richtung) =>
    setSortierung((alt) =>
      alt.spalte === spalte
        ? { spalte, richtung: alt.richtung === "auf" ? "ab" : "auf" }
        : { spalte, richtung: ersteRichtung },
    );
  return [sortierung, umschalten] as const;
}

/** Für `aria-sort` an der Kopfzelle: Vorleseprogramme sagen dann die Richtung an. */
export function ariaSort(aktiv: boolean, richtung: Richtung): "ascending" | "descending" | "none" {
  return aktiv ? (richtung === "auf" ? "ascending" : "descending") : "none";
}

export function SortKnopf({
  aktiv,
  richtung,
  onClick,
  children,
}: {
  aktiv: boolean;
  richtung: Richtung;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 uppercase hover:text-ink ${aktiv ? "text-ink" : ""}`}
      title="Sortieren; ein zweiter Klick dreht die Richtung"
    >
      {children}
      <span aria-hidden className={aktiv ? "" : "opacity-30"}>
        {aktiv ? (richtung === "auf" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

export function Suchfeld({
  wert,
  onChange,
  platzhalter,
}: {
  wert: string;
  onChange: (wert: string) => void;
  platzhalter: string;
}) {
  return (
    <input
      type="search"
      className="field w-auto min-w-[14rem] py-1.5"
      value={wert}
      onChange={(e) => onChange(e.target.value)}
      placeholder={platzhalter}
      aria-label={platzhalter}
    />
  );
}
